import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';

import type { Template } from '@feathr/blackbox';
import type { ITanStackColumnMeta } from '@feathr/components';
import {
  createActionMenuColumn,
  DateTimeCell,
  ImageThumbnailCell,
  LinkNameCell,
  Pill,
  PlaceholderImage,
  Spinner,
  TanStackHeader,
} from '@feathr/components';
import { useLocalUrl } from '@feathr/extender/state';
import type { TTemplateGroup } from '@feathr/extender/styles/template';
import {
  templateGroupLabel,
  templateGroupTheme,
  templateTypeIcon,
  templateTypeTheme,
} from '@feathr/extender/styles/template';
import { TimeFormat } from '@feathr/hooks';

import EventTemplatesGlobalOptions from './EventTemplatesGlobalOptions';

import * as styles from './eventTemplatesGlobalColumns.css';

const THUMBNAIL_WIDTH = 220;
const THUMBNAIL_HEIGHT = 100;

function getTemplateGroup(template: Template): TTemplateGroup | undefined {
  if (template.get('default')) {
    return 'default';
  }
  const parentKind = template.get('parent_kind');
  if (parentKind === 'account') {
    return 'account';
  }
  if (parentKind === 'event') {
    return 'project';
  }
  return undefined;
}

const ACTION_MENU_COLUMN_SHAPE = createActionMenuColumn<Template>(() => null);

/**
 * Column definitions for the project's Saved Templates table.
 *
 * Each sortable column `id` is the field the list route orders by. Every cell reads the model
 * through `Observer`, because `TanStackTable` calls these callbacks outside the page's `observer`.
 */
export default function eventTemplatesGlobalColumns(t: TFunction): Array<ColumnDef<Template>> {
  return [
    {
      cell: ({ row }: CellContext<Template, unknown>): JSX.Element => (
        <Observer>
          {function useAnonymousFunction(): JSX.Element {
            const localUrl = useLocalUrl();
            const template = row.original;
            const thumbnail =
              template.isBanner && template.shouldUsePlaceholder() ? (
                // Drawn at the banner's own size, because the placeholder prints its size.
                <PlaceholderImage
                  className={styles.placeholder}
                  height={template.get('height') ?? THUMBNAIL_HEIGHT}
                  width={template.get('width') ?? THUMBNAIL_WIDTH}
                />
              ) : (
                <ImageThumbnailCell
                  alt={''}
                  height={THUMBNAIL_HEIGHT}
                  src={template.get('thumbnail_url')}
                  width={THUMBNAIL_WIDTH}
                />
              );
            // The name link beside it goes to the same page, so screen readers skip this one.
            return (
              <Link aria-hidden={true} tabIndex={-1} to={localUrl(template.getItemUrl())}>
                {thumbnail}
              </Link>
            );
          }}
        </Observer>
      ),
      enableSorting: false,
      header: (): JSX.Element => <TanStackHeader title={t('Thumbnail')} />,
      id: 'thumbnail',
      size: THUMBNAIL_WIDTH,
    },
    {
      accessorFn: (row): string => row.name,
      cell: ({ row }: CellContext<Template, unknown>): JSX.Element => (
        <Observer>
          {function useAnonymousFunction(): JSX.Element {
            const localUrl = useLocalUrl();
            const template = row.original;
            return (
              <LinkNameCell
                label={template.name}
                to={localUrl(template.getItemUrl())}
                trailing={template.isUpdating ? <Spinner size={16} /> : undefined}
              />
            );
          }}
        </Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Template')} />,
      id: 'name',
      meta: { isFlex: true, isPrimary: true } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    {
      cell: ({ row }: CellContext<Template, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => {
            const cls = row.original.get('_cls');
            const typeIcon = templateTypeIcon(cls);
            return (
              <Pill
                prefix={typeIcon ? <FontAwesomeIcon icon={typeIcon} /> : undefined}
                theme={templateTypeTheme(cls)}
              >
                {row.original.getTypeLabel()}
              </Pill>
            );
          }}
        </Observer>
      ),
      enableSorting: false,
      header: (): JSX.Element => <TanStackHeader title={t('Type')} />,
      id: '_cls',
    },
    {
      cell: ({ row }: CellContext<Template, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => {
            const group = getTemplateGroup(row.original);
            if (!group) {
              return <></>;
            }
            return <Pill theme={templateGroupTheme(group)}>{templateGroupLabel(group, t)}</Pill>;
          }}
        </Observer>
      ),
      enableSorting: false,
      header: (): JSX.Element => <TanStackHeader title={t('Group')} />,
      id: 'group',
    },
    {
      accessorFn: (row): string | undefined => row.get('date_last_modified'),
      cell: ({ row }: CellContext<Template, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => (
            <DateTimeCell
              format={TimeFormat.shortDateTime}
              value={row.original.get('date_last_modified')}
            />
          )}
        </Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Last Updated')} />,
      id: 'date_last_modified',
    },
    {
      ...ACTION_MENU_COLUMN_SHAPE,
      cell: ({ row }: CellContext<Template, unknown>): JSX.Element => (
        <EventTemplatesGlobalOptions template={row.original} />
      ),
    },
  ];
}
